import { useState, useMemo, useEffect } from 'react';
import {
  Table2,
  Plus,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Share2,
  Lock,
  Users,
  Globe,
  Eye,
  FileCode,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/utils/dateFormatter';
import { useViewportPageSize } from '@/hooks/use-viewport-page-size';
import type { DataTable } from '@/types/data-tables';

interface DataTableListViewProps {
  tables: DataTable[];
  loading: boolean;
  searchQuery?: string;
  onTableClick: (table: DataTable) => void;
  onEditTable?: (table: DataTable) => void;
  onDeleteTable?: (table: DataTable) => void;
  onShareTable?: (table: DataTable) => void;
  onViewData?: (table: DataTable) => void;
  onViewSchema?: (table: DataTable) => void;
  onCreateTable: () => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
}

// Helper component for visibility badge
function VisibilityBadge({ table }: { table: DataTable }) {
  const getVisibilityInfo = () => {
    switch (table.visibility) {
      case 'public':
        return { icon: Globe, label: 'Public', variant: 'outline' as const };
      case 'team':
        const teamNames = table.sharedTeams?.map((t) => t.teamName).join(', ') || 'Teams';
        return { icon: Users, label: teamNames, variant: 'secondary' as const };
      default:
        return { icon: Lock, label: 'Private', variant: 'secondary' as const };
    }
  };

  const { icon: Icon, label, variant } = getVisibilityInfo();
  const isTeam = table.visibility === 'team';
  const displayLabel = isTeam && label.length > 20 ? label.substring(0, 17) + '...' : label;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={variant} className="gap-1 cursor-default">
            <Icon className="h-3 w-3" />
            <span className="truncate max-w-[100px]">{displayLabel}</span>
          </Badge>
        </TooltipTrigger>
        {isTeam && table.sharedTeams && table.sharedTeams.length > 0 && (
          <TooltipContent>
            <p>Shared with: {table.sharedTeams.map((t) => t.teamName).join(', ')}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

// Skeleton component for loading state
function DataTableListSkeleton() {
  return (
    <div className="border rounded-lg bg-card shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[35%]">Name</TableHead>
            <TableHead className="w-[15%]">Visibility</TableHead>
            <TableHead className="w-[10%]">Rows</TableHead>
            <TableHead className="w-[10%]">Columns</TableHead>
            <TableHead className="w-[15%]">Updated</TableHead>
            <TableHead className="w-[15%] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-12" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-8" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-24" />
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function DataTableListView({
  tables,
  loading,
  searchQuery = '',
  onTableClick,
  onEditTable,
  onDeleteTable,
  onShareTable,
  onViewData,
  onViewSchema,
  onCreateTable,
  emptyStateTitle = 'No data tables yet',
  emptyStateDescription = 'Create your first data table to get started',
}: DataTableListViewProps) {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = useViewportPageSize({
    rowHeight: 72,
    minPageSize: 5,
    maxPageSize: 50,
    bufferSpace: 5,
    loading,            // Recalculate when loading completes
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState<'name' | 'rows' | 'updated'>('updated');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  // Filter and sort tables
  const filteredTables = useMemo(() => {
    const filtered = tables.filter((table) => {
      if (!searchQuery) return true;
      const matchesSearch =
        table.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        table.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });

    // Sort the filtered results
    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'rows':
          comparison = a.rowCount - b.rowCount;
          break;
        case 'updated':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [tables, searchQuery, sortColumn, sortDirection]);

  // Paginated tables
  const paginatedTables = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredTables.slice(startIndex, endIndex);
  }, [filteredTables, currentPage, pageSize]);

  // Total pages
  const totalPages = Math.ceil(filteredTables.length / pageSize);

  // Handle column sort
  const handleSort = (column: 'name' | 'rows' | 'updated') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-auto">
          <DataTableListSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Table or Empty State */}
      {filteredTables.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-12 text-center">
          <div className="rounded-full bg-muted p-3 mb-4">
            <Table2 className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">
            {searchQuery ? 'No tables found' : emptyStateTitle}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery
              ? 'Try adjusting your search query'
              : emptyStateDescription}
          </p>
          {!searchQuery && (
            <Button onClick={onCreateTable}>
              <Plus className="w-4 h-4 mr-2" />
              New Table
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Table - Scrollable */}
          <div className="flex-1 overflow-auto">
            <div className="border rounded-lg bg-card shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[35%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('name')}
                      >
                        Name
                        {sortColumn === 'name' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%]">Visibility</TableHead>
                    <TableHead className="w-[10%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('rows')}
                      >
                        Rows
                        {sortColumn === 'rows' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[10%]">Columns</TableHead>
                    <TableHead className="w-[15%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('updated')}
                      >
                        Updated
                        {sortColumn === 'updated' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTables.map((table) => (
                    <TableRow
                      key={table.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => onTableClick(table)}
                    >
                      <TableCell>
                        <div className={`flex flex-col gap-0.5 h-[40px] ${!table.description ? 'justify-center' : ''}`}>
                          <div className="flex items-center gap-2">
                            <Table2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium">{table.name}</span>
                          </div>
                          {table.description && (
                            <span className="text-sm text-muted-foreground truncate max-w-md ml-6">
                              {table.description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <VisibilityBadge table={table} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{table.rowCount.toLocaleString()}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{table.schema.columns.length}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(table.updatedAt)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* View Data button */}
                          {onViewData && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onViewData(table);
                                    }}
                                  >
                                    <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Preview data</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {/* View Schema button */}
                          {onViewSchema && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onViewSchema(table);
                                    }}
                                  >
                                    <FileCode className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View schema</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {table.isOwner !== false && (
                            <>
                              {/* Share button */}
                              {onShareTable && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onShareTable(table);
                                        }}
                                      >
                                        <Share2 className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Share settings</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {/* Edit button */}
                              {onEditTable && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEditTable(table);
                                        }}
                                      >
                                        <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Edit table</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {/* Delete button */}
                              {onDeleteTable && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onDeleteTable(table);
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Delete table</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination Footer - Fixed at bottom */}
          {filteredTables.length > 0 && (
            <div className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-1.5 bg-card border rounded-lg mt-2">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Showing {(currentPage - 1) * pageSize + 1} to{' '}
                {Math.min(currentPage * pageSize, filteredTables.length)} of{' '}
                {filteredTables.length} tables
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </Button>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium whitespace-nowrap">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
